import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Line } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register necessary components
Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const CustomerBMICalculator = () => {
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [bmi, setBmi] = useState(null);
  const [bmiHistory, setBmiHistory] = useState([]);
  const [lastCalculated, setLastCalculated] = useState(null);
  
  // Simulated data for BMI history
  const initialBMIHistory = [
    { date: '2024-01-01', bmi: 22 },
    { date: '2024-02-01', bmi: 23 },
    { date: '2024-03-01', bmi: 24 },
  ];

  useEffect(() => {
    // Load initial BMI history
    setBmiHistory(initialBMIHistory);
  }, []);

  const calculateBMI = () => {
    if (height && weight) {
      const heightInMeters = height / 100; // Convert cm to meters
      const calculatedBmi = (weight / (heightInMeters * heightInMeters)).toFixed(2);
      setBmi(calculatedBmi);
      setLastCalculated(new Date());
      
      // Update BMI history
      setBmiHistory((prevHistory) => [
        ...prevHistory,
        { date: format(new Date(), 'yyyy-MM-dd'), bmi: calculatedBmi }
      ]);
    }
  };

  const isCalculable = () => {
    if (!lastCalculated) return true; // Can calculate if never calculated
    const now = new Date();
    return lastCalculated.toDateString() !== now.toDateString();
  };

  const bmiCondition = () => {
    if (!bmi) return 'N/A';
    const bmiValue = parseFloat(bmi);
    if (bmiValue < 18.5) return 'Underweight';
    if (bmiValue < 24.9) return 'Normal';
    if (bmiValue < 29.9) return 'Overweight';
    return 'Obese';
  };

  // Chart data
  const chartData = {
    labels: bmiHistory.map(entry => entry.date),
    datasets: [
      {
        label: 'BMI Over Time',
        data: bmiHistory.map(entry => entry.bmi),
        borderColor: 'rgba(255, 206, 86, 1)',
        backgroundColor: 'rgba(255, 206, 86, 0.2)',
        fill: true,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-8">BMI Calculator</h2>
        <p className="text-center mb-4">{format(new Date(), 'PP')} - Today's Date</p>

        {/* Input Fields */}
        <div className="flex justify-center space-x-4 mb-8">
          <input
            type="number"
            placeholder="Height (cm)"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            className="p-2 rounded-lg bg-gray-800"
          />
          <input
            type="number"
            placeholder="Weight (kg)"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="p-2 rounded-lg bg-gray-800"
          />
          <button
            onClick={calculateBMI}
            disabled={!isCalculable()}
            className={`p-2 rounded-lg ${isCalculable() ? 'bg-yellow-400' : 'bg-gray-600 cursor-not-allowed'}`}
          >
            Calculate
          </button>
        </div>

        {/* BMI Condition Card */}
        <div className="bg-gray-800 p-4 rounded-lg mb-8">
          <h3 className="text-2xl mb-2">Your Current BMI</h3>
          <p className="text-4xl">{bmi || 'N/A'}</p>
          <p className="text-lg">Condition: {bmiCondition()}</p>
        </div>

        {/* Chart */}
        <div className="bg-gray-800 p-4 rounded-lg">
          <h3 className="text-2xl mb-4">BMI History</h3>
          <Line data={chartData} />
        </div>
      </div>
    </div>
  );
};

export default CustomerBMICalculator;
